// /components/games/SequenceMemory.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  CssBaseline,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";

const GRID_SIZE = 3;

const SequenceMemory = () => {
  const [sequence, setSequence] = useState([]);
  const [playerInput, setPlayerInput] = useState([]);
  const [flashing, setFlashing] = useState(false);
  const [level, setLevel] = useState(0);
  const [score, setScore] = useState(0);
  const [themeMode, setThemeMode] = useState("light");
  const [gameOver, setGameOver] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);

  const theme = createTheme({
    palette: {
      mode: themeMode,
      primary: { main: themeMode === "light" ? "#1976d2" : "#64b5f6" },
      secondary: { main: "#ff9800" },
      background: {
        default: themeMode === "light" ? "#f4f7fa" : "#0d1117",
        paper: themeMode === "light" ? "#fff" : "#161b22",
      },
    },
    shape: { borderRadius: 12 },
    typography: { fontFamily: "Inter, sans-serif" },
  });

  const getRandomTile = () => Math.floor(Math.random() * GRID_SIZE ** 2);

  const startGame = () => {
    setSequence([getRandomTile()]);
    setPlayerInput([]);
    setLevel(1);
    setScore(0);
    setGameOver(false);
  };

  const flashSequence = async (seq) => {
    setFlashing(true);
    for (let i = 0; i < seq.length; i++) {
      setActiveIndex(seq[i]);
      await new Promise((r) => setTimeout(r, 500));
      setActiveIndex(null);
      await new Promise((r) => setTimeout(r, 250));
    }
    setFlashing(false);
  };

  const handleTileClick = (index) => {
    if (flashing || gameOver || level === 0) return;

    const newInput = [...playerInput, index];
    setPlayerInput(newInput);

    const currentStep = newInput.length - 1;
    if (index !== sequence[currentStep]) {
      setGameOver(true);
      return;
    }

    if (newInput.length === sequence.length) {
      setScore((s) => s + 1);
      const nextSequence = [...sequence, getRandomTile()];
      setSequence(nextSequence);
      setPlayerInput([]);

      setTimeout(() => {
        flashSequence(nextSequence);
        setLevel((l) => l + 1);
      }, 800);
    }
  };

  useEffect(() => {
    if (sequence.length > 0 && level === 1) {
      flashSequence(sequence);
    }
  }, [sequence]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background:
            themeMode === "light"
              ? "linear-gradient(135deg,#f0f4ff,#c3d9ff)"
              : "linear-gradient(135deg,#0f2027,#203a43,#2c5364)",
          transition: "0.3s",
          py: 4,
        }}
      >
        <Card
          sx={{
            p: 3,
            borderRadius: 4,
            boxShadow: 8,
            width: 340,
            textAlign: "center",
          }}
        >
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h5" fontWeight="bold">
                🧩 Sequence Memory
              </Typography>
              <Button
                onClick={() =>
                  setThemeMode(themeMode === "light" ? "dark" : "light")
                }
              >
                {themeMode === "light" ? "Dark" : "Light"}
              </Button>
            </Box>

            <Typography variant="h6" sx={{ mt: 2 }}>
              Level: {level} | Score: {score}
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: `repeat(${GRID_SIZE}, 1fr)`,
                gap: 1.5,
                mt: 3,
                justifyItems: "center",
              }}
            >
              {[...Array(GRID_SIZE ** 2)].map((_, i) => (
                <Box
                  key={i}
                  onClick={() => handleTileClick(i)}
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: 2,
                    backgroundColor:
                      activeIndex === i
                        ? "primary.main"
                        : themeMode === "light"
                        ? "#e0e0e0"
                        : "#2a2f34",
                    boxShadow:
                      activeIndex === i
                        ? "0 0 15px rgba(25,118,210,0.7)"
                        : "inset 0 0 4px rgba(0,0,0,0.3)",
                    cursor: flashing || gameOver || level === 0 ? "default" : "pointer",
                    transition: "background-color 0.2s, box-shadow 0.2s",
                  }}
                />
              ))}
            </Box>

            {gameOver && (
              <Typography
                variant="h6"
                color="error"
                sx={{ mt: 3, fontWeight: "bold" }}
              >
                ❌ Wrong! Game Over.
              </Typography>
            )}

            <Box sx={{ mt: 3 }}>
              {level === 0 || gameOver ? (
                <Button variant="contained" onClick={startGame}>
                  Start
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setGameOver(true)}
                >
                  Stop
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </ThemeProvider>
  );
};

export default SequenceMemory;
